import { cn } from '@/lib/utils';
import { Button } from '@/components/ui-kit/button';
import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/components/ui-kit/sheet';
import {
  Building03Icon,
  ContactBookIcon,
  DashboardSquare03Icon,
  FileValidationIcon,
  LicenseDraftIcon,
  LicenseIcon,
  Settings01Icon,
  Trash2,
  Unarchive03Icon,
  UserGroupIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Building2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { OrgRole } from '../org-permissions/types/permissions.types';
import NewDocumentDialog from './new-document-dialog';

const allNavItems = [
  {
    href: '',
    labelKey: 'OVERVIEW',
    icon: DashboardSquare03Icon,
    roles: ['org_admin', 'org_editor', 'org_member'] as OrgRole[],
  },
  {
    href: '/contracts',
    labelKey: 'CONTRACTS',
    icon: LicenseDraftIcon,
    roles: ['org_admin', 'org_editor', 'org_member'] as OrgRole[],
  },
  {
    href: '/contract-checker',
    labelKey: 'CONTRACTCHECKER',
    icon: FileValidationIcon,
    roles: ['org_admin', 'org_editor', 'org_member'] as OrgRole[],
  },
  {
    href: '/templates',
    labelKey: 'TEMPLATES',
    icon: LicenseIcon,
    roles: ['org_admin', 'org_editor', 'org_member'] as OrgRole[],
  },
  {
    href: '/contacts',
    labelKey: 'CONTACTS_MANAGER',
    icon: ContactBookIcon,
    roles: ['org_admin', 'org_editor', 'org_member'] as OrgRole[],
  },
  {
    href: '/companies',
    labelKey: 'COMPANIES',
    icon: Building03Icon,
    roles: ['org_admin', 'org_editor', 'org_member'] as OrgRole[],
  },
  {
    href: '/users',
    labelKey: 'TEAM',
    icon: UserGroupIcon,
    roles: ['org_admin'] as OrgRole[],
  },
  {
    href: '/archive',
    labelKey: 'ARCHIVE',
    icon: Unarchive03Icon,
    roles: ['org_admin', 'org_editor', 'org_member'] as OrgRole[],
  },
  {
    href: '/trash',
    labelKey: 'TRASH',
    icon: Trash2,
    roles: ['org_admin', 'org_editor', 'org_member'] as OrgRole[],
  },
  {
    href: '/settings',
    labelKey: 'SETTINGS',
    icon: Settings01Icon,
    roles: ['org_admin'] as OrgRole[],
  },
];

interface OrgSidebarProps {
  orgSlug: string;
  orgName: string;
  orgRole: OrgRole;
  /** When provided, renders as a Radix Dialog–backed mobile drawer (Sheet). */
  isMobile?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function OrgSidebar({
  orgSlug,
  orgName,
  orgRole,
  isMobile = false,
  open,
  onOpenChange,
}: OrgSidebarProps) {
  const pathname = useLocation().pathname;
  const { t } = useTranslation();

  // Filter nav items based on user role
  const navItems = allNavItems.filter((item) => item.roles.includes(orgRole));

  const basePath = `/org/${orgSlug}`;
  const close = () => onOpenChange?.(false);

  const isActive = (href: string) => {
    const fullPath = `${basePath}${href}`;
    if (href === '') {
      return pathname === basePath || pathname === `${basePath}/`;
    }
    return pathname.startsWith(fullPath);
  };

  const sidebarContent = (
    <div
      className={cn(
        'flex flex-col h-full bg-[hsl(var(--bg))] border-border relative',
        // Border-right only matters in desktop docked mode; SheetContent
        // already supplies its own border-l/r.
        !isMobile && 'w-64 border-r',
        isMobile && 'w-full'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 h-16 px-4">
        {!isMobile && <NewDocumentDialog />}

        {isMobile && (
          <>
            <Link
              to={basePath}
              onClick={close}
              className="flex items-center gap-2 text-lg font-semibold text-selise-blue font-heading truncate min-w-0"
            >
              <Building2 className="h-5 w-5 shrink-0" />
              <span className="truncate">{orgName}</span>
            </Link>
            <SheetClose asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close sidebar"
                className="h-9 w-9 shrink-0"
              >
                <X className="h-5 w-5" />
              </Button>
            </SheetClose>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return (
            <Link
              key={item.href || 'overview'}
              to={`${basePath}${item.href}`}
              onClick={isMobile ? close : undefined}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                active
                  ? 'text-selise-blue-500 bg-selise-blue/10 font-semibold'
                  : 'text-globe-grey-500 hover:text-selise-blue hover:bg-muted'
              )}
            >
              <HugeiconsIcon icon={Icon} size={20} />
              <span>{t(`ORGSIDEBAR_NAV_${item.labelKey}`)}</span>
            </Link>
          );
        })}
      </nav>

      {/* Back to Templates */}
      <div className="p-4 border-t border-border flex items-center justify-center md:hidden">
        <NewDocumentDialog className="w-full" />
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="left" hideClose className="w-80 max-w-[85vw] p-0 sm:max-w-sm md:hidden">
          {/* SheetTitle is required by Radix for a11y; visually hidden. */}
          <SheetTitle className="sr-only">{orgName}</SheetTitle>
          {sidebarContent}
        </SheetContent>
      </Sheet>
    );
  }

  return sidebarContent;
}
